import analytics from '../utils/analytics';
import { useEffect } from 'react';
import { resolveAnalyticsUrl } from '../utils/content-analytics-resources';
import type { ContentLinkAnalytics } from '../components/atlas-link/track';

/**
 * Hook to track content link viewed analytics events
 *
 * This hook automatically tracks when content links are viewed and can be used
 * across different contexts (search results, sidebar, browse pages, etc.)
 *
 * @param params - The analytics parameters for tracking content link views
 *
 * @example
 * ```tsx
 * // For search results
 * useContentLinkViewedAnalytics({
 *   payload: {
 *     linkType: 'card',
 *     context: 'search-results',
 *     position: 1,
 *     linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *     linkContentResolvedUrl: 'https://example.com/doc/123'
 *   },
 *   shouldSkip: false
 * });
 *
 * // For sidebar content with parent page type
 * useContentLinkViewedAnalytics({
 *   payload: {
 *     linkType: 'text-link',
 *     context: 'sidebar',
 *     position: 2,
 *     linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *     linkContentResolvedUrl: 'https://example.com/doc/789',
 *     parentPageType: 'topic'
 *   },
 *   shouldSkip: false
 * });
 *
 * // For browse content with parent page type
 * useContentLinkViewedAnalytics({
 *   payload: {
 *     linkType: 'card',
 *     context: 'browse',
 *     position: 3,
 *     linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry456',
 *     linkContentResolvedUrl: 'https://example.com/doc/456',
 *     parentPageType: 'topic-set'
 *   },
 *   shouldSkip: false
 * });
 *
 * // To skip tracking (e.g., for hidden items), use shouldSkip flag
 * useContentLinkViewedAnalytics({
 *   shouldSkip: true
 * });
 *
 * // Or conditionally skip tracking based on context
 * useContentLinkViewedAnalytics({
 *   payload: {
 *     linkType: 'text-link',
 *     context: 'sidebar',
 *     position: 1,
 *     linkContentAri: 'ari:third-party:contentful::entry/space123/env456/entry789',
 *     linkContentResolvedUrl: 'https://example.com/doc/789'
 *   },
 *   shouldSkip: isCurrentPage // Skip if this is the current page
 * });
 * ```
 */

export const useContentLinkViewedAnalytics = (
    params: ContentLinkAnalytics
): void => {
    const payload = params.payload;
    const shouldSkip = params.shouldSkip || !payload;
    // Extract dependency variables outside useEffect to avoid complex expressions in dependency array
    // These must be non-null for analytics to be tracked
    const linkType = payload?.linkType;
    const context = payload?.context;
    const position = payload?.position;
    const linkContentAri = payload?.linkContentAri;
    const linkContentResolvedUrl = payload?.linkContentResolvedUrl;
    const parentPageType = payload?.parentPageType;

    useEffect(() => {
        // Skip tracking if params is null, shouldSkip is true, or if required fields are missing
        if (shouldSkip) {
            return;
        }

        // Resolve the URL properly on client-side
        const resolvedUrl = resolveAnalyticsUrl(linkContentResolvedUrl);

        // Build the base analytics properties using UI event structure
        const analyticsProperties = {
            event: 'viewed',
            eventComponent: 'content-link',
            eventContainer: context,
            // Required properties for content link tracking
            position,
            link_type: linkType,
            link_content_ari: linkContentAri,
            link_content_resolved_url: resolvedUrl,
            // Include parent page type if provided
            ...(parentPageType && { parent_page_type: parentPageType })
        };

        analytics.trackEvent('content-link viewed', analyticsProperties);
    }, [
        shouldSkip,
        linkType,
        context,
        position,
        linkContentAri,
        linkContentResolvedUrl,
        parentPageType
    ]);
};
